<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { reactive } from 'vue'
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useRouter } from 'vue-router'

const backend = new Backend()
const formProcess = new FormProcess()
const gameStore = useGameStore()
const router = useRouter()

// Define the validation schema
const schema = yup.object().shape({
  allianceName: yup
    .string()
    .min(3, () => `Use at least 3 characters`)
    .max(32, () => `Do not use more than 32 characters`)
    .required(() => `This is required`),
  allianceDescription: yup.string().max(1024, () => `Do not use more than 1024 characters`)
})

// Default form values
const formValues = reactive<{ [key: string]: string }>({
  allianceName: '',
  allianceDescription: ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  allianceName: '',
  allianceDescription: ''
})

// Handle form submission
const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)

      backend
        .createAlliance(formValues.allianceName, formValues.allianceDescription)
        .then(() => {
          gameStore.setTinySuccessToastMessage('Alliance created')
          gameStore.modalType = ''
          // redirect to alliances page
          router.push('/diplomacy/alliance')
        })
        .catch((error) => formProcess.processBackendErrorResponse(error, formErrors))
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}
</script>

<template>
  <SmallModel title="Create Alliance">
    <form method="post" @submit.prevent="submit" class="p-3">
      <FormInput
        label="Alliance name"
        v-model="formValues.allianceName"
        :error="formErrors.allianceName"
        :autoFocus="true"
      />

      <div class="mb-3">
        <label for="allianceDescription" class="block mb-1 text-sm font-medium text-slate-300"
          >Alliance description</label
        >
        <textarea
          id="allianceDescription"
          v-model="formValues.allianceDescription"
          rows="4"
          class="border text-sm rounded-lg block w-full p-2.5 bg-slate-800 border-slate-700 text-white focus:ring-blue-500 focus:border-blue-500"
          :class="{ 'border-red-500': formErrors.allianceDescription }"
        ></textarea>
        <p v-if="formErrors.allianceDescription" class="mt-1 text-xs text-red-500">
          {{ formErrors.allianceDescription }}
        </p>
      </div>

      <div class="flex justify-between pt-2">
        <FormGenericError :error="formErrors.generic" />
        <ActionButton type="submit">Create Alliance</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>

<style scoped lang="scss"></style>
